import { useMemo } from "react";
import {
  TextField,
  DatagridConfigurable,
  useTheme,
  NumberField,
  FunctionField,
  RecordContextProvider,
  ChipField,
} from "react-admin";
import getFirstAndLastDateOfCurrentMonth from "../../utils/firstLastDate";
import DownloadAction from "../../components/tableListActions/DownloadActionNew";
import inventoryAgeFilterList from "../../components/inventoryAges/filter/inventoryAgeFilterList";
import MyDatagridBody from "../../components/common/MyDatagridBody";
import InventoryAgeFooterList from "../../components/inventoryAges/footer/InventoryAgeFooterList";
import useCompanyScopedListFilters from "../../hooks/useCompanyScopedListFilters";
import ManualReportList from "../../components/reports/ManualReportList";

const [firstDate, lastDate] = getFirstAndLastDateOfCurrentMonth();
const inventoryAgeDefaultFilters = {
  start_date: firstDate,
  end_date: lastDate,
  cut_off: lastDate,
};

const InventoryAgeList = () => {
  const { permissions, canSeeCompanyColumn, filterDefaultValues } =
    useCompanyScopedListFilters(inventoryAgeDefaultFilters);
  const [theme] = useTheme("light");

  const list = useMemo(
    () => (
      <ManualReportList
        storeKey={false} // Disabling Parameters Persistence
        title="mine.list_title.inventory_age"
        empty={false}
        // aside={<InventoryCardAsideList />}
        actions={<DownloadAction />}
        filters={inventoryAgeFilterList(permissions)}
        filterDefaultValues={filterDefaultValues}
        requiredFilters={["start_date", "end_date", "cut_off"]}
        queryOptions={{ meta: { sort: ["Product.code ASC"] } }} // overide default sort
        sx={{
          flexDirection: "column",
        }}
      >
        <DatagridConfigurable
          body={
            <MyDatagridBody
              footer={<InventoryAgeFooterList canSeeCompanyColumn={canSeeCompanyColumn} />}
            />
          }
          bulkActionButtons={false}
          // rowClick={rowClickHandler}
          // expand={<InventoryCardExpandList />}
          sx={{
            "& .RaDatagrid-expandedPanel > td": {
              p: 0,
              bgcolor: theme !== "dark" ? "#E7EBF0" : "grey.800",
            },
          }}
        >
          {canSeeCompanyColumn ? (
            <TextField source="company.alias" label="mine.label.depo" />
          ) : null}
          <TextField source="product.code" label="mine.label.product_code" />
          <FunctionField
            textAlign="center"
            label="mine.label.brand"
            render={(record) => {
              return (
                <RecordContextProvider
                  value={{
                    brand: !record.product.kind.dynamic_code
                      ? record.product.code.substr(0, 1)
                      : "-",
                  }}
                >
                  <TextField source="brand" />
                </RecordContextProvider>
              );
            }}
          />
          <FunctionField
            textAlign="center"
            label="mine.label.size"
            render={(record) => {
              return (
                <RecordContextProvider
                  value={{
                    size: !record.product.kind.dynamic_code
                      ? record.product.code.substr(1, 2)
                      : "-",
                  }}
                >
                  <TextField source="size" />
                </RecordContextProvider>
              );
            }}
          />
          <TextField source="product.kind.name" label="mine.label.kind" />
          <NumberField
            source="remaining_quantity"
            label="mine.label.quantity"
          />
          <NumberField source="total_cogs" label="mine.label.value" />
          <NumberField source="age" label="mine.label.age" />
          <FunctionField
            label="Status"
            textAlign="center"
            render={(record) => {
              let rec = { status: record.status, color: "default" };
              if (record.status.includes(">")) {
                rec.color = "error";
              }
              return (
                <RecordContextProvider value={rec}>
                  <ChipField source="status" color={rec.color} size="small" />
                </RecordContextProvider>
              );
            }}
          />
        </DatagridConfigurable>
      </ManualReportList>
    ),
    [theme, permissions, filterDefaultValues, canSeeCompanyColumn]
  );

  return list;
};

export default InventoryAgeList;
